/** Later (6): running it as a team. Board learn-r2, missions 15 to 20. */
import type { Mission } from '../missions'
import { FLAG_US, savedOnly, selfTick } from './shared'

export const later: Mission[] = [
  {
    id: 'keep-your-app-tidy',
    order: 15,
    title: 'Keep your app tidy',
    track: 'later',
    say: 'Is my app tidy? Run the house checks and tell me what they say in plain words. Suggest any screen to retire.',
    happens: [
      'Your agent follows its tidy skill and runs bun run check: types, tests, the house rules and the build.',
      'It tells you what passed, what failed and why, in plain words, one rule at a time.',
      'It suggests screens nobody uses, or two that do the same job.',
      'It retires a screen only when you say so, and takes out all of its pieces together.',
    ],
    check: { kind: 'self' },
    watching: 'Nothing on this page. Tick it once your agent has answered.',
    ifNot: [
      { symptom: 'A rule fails.', say: 'Fix it without changing the rule. If the rule is wrong for us, tell me why first.' },
      { symptom: 'It wants to delete a screen right away.', say: "Don't delete anything yet. Tell me what it does and who uses it." },
      FLAG_US,
    ],
    how: selfTick('Keep your app tidy', "This page can't see your chat."),
    skill: 'tidy',
    recipe: 'keep-your-app-tidy',
    lesson: `**The house rules, plainly.** Your app checks these every time it ships:

- Every screen is built from the shared parts in src/ui.
- Nothing styles itself. The look lives in src/ui and your brand file.
- Every screen is on the screen list and has a test for its numbers.
- Keys never go in the code.
- Drafts stay hidden from people you share with until you say they're ready.`,
  },
  {
    id: 'teach-your-agent-a-rule',
    order: 16,
    title: 'Teach your agent a rule',
    track: 'later',
    say: 'Remember for every chat: we call wholesale accounts doors, and our week starts on Monday.',
    happens: [
      'Your agent writes your own skill, in a new folder in .claude/skills.',
      "cpg.computer's skills are put back if they're changed. Yours are kept.",
      'Every new chat reads it.',
      'Start a new chat and ask "What do we call wholesale accounts?" to check.',
    ],
    check: { kind: 'self' },
    watching: 'Nothing on this page. Tick it once a new chat knows the rule.',
    ifNot: [
      { symptom: "The agent edited one of cpg.computer's skills.", say: 'Put that skill back, and write the rule in a new skill of our own.' },
      { symptom: 'A new chat forgot the rule.', say: 'Where did you write the rule? Every new chat should read it.' },
      FLAG_US,
    ],
    how: selfTick('Teach your agent a rule', "This page can't see your chats."),
    recipe: 'teach-a-rule',
  },
  {
    id: 'invite-a-teammate',
    order: 17,
    title: 'Invite a teammate',
    track: 'later',
    act: 'do',
    say: 'Invite a teammate from your cpg.computer dashboard. They set up their own sign-in and their own agent.',
    happens: [
      'You add their email in your dashboard.',
      'They get an invite, sign in, and run their own setup.',
      'They pair their phone and work with their own agent on the same app.',
      'Each of you has your own Learn progress.',
    ],
    check: { kind: 'signal', signal: 'hasTeammate' },
    watching: 'A yes or no from cpg.computer: is there a teammate on your app?',
    ifNot: [FLAG_US],
    how: `cpg.computer knows who is on your team. The page asks for a yes or no. ${savedOnly('Invite a teammate')}`,
    recipe: 'invite-a-teammate',
    needs: 'Waits on inviting from your dashboard.',
  },
  {
    id: 'switch-coding-agents',
    order: 18,
    title: 'Switch coding agents',
    track: 'later',
    act: 'do',
    say: 'Press "Switch coding agent" on your dashboard. Then ask the new agent: "Read this app\'s guide and tell me what it does."',
    happens: [
      'You pick another coding agent in your dashboard.',
      "Your computer sets it up. Your app and its history don't change.",
      'The new agent reads the same guide (AGENTS.md) and the same skills.',
      'It tells you what the app does, in the same plain words.',
    ],
    check: { kind: 'signal', signal: 'agentSwitched' },
    watching: 'A yes or no from cpg.computer: did your coding agent change?',
    ifNot: [FLAG_US],
    how: `cpg.computer knows which agent you use. The page asks for a yes or no. ${savedOnly('Switch coding agents')}`,
    recipe: 'switch-agents',
    needs: 'Waits on "Switch coding agent" in your dashboard.',
  },
  {
    id: 'run-setup-again',
    order: 19,
    title: 'Run setup again',
    track: 'later',
    act: 'do',
    say: 'Press "Run setup again" on your cpg.computer dashboard and walk through it.',
    happens: [
      'Setup starts from the first step, like a tutorial.',
      'Nothing on your computer or in your app is removed.',
      'Steps that are already done pass straight away.',
      'You land back on your dashboard.',
    ],
    check: { kind: 'signal', signal: 'setupRerun' },
    watching: 'A yes or no from cpg.computer: did you run setup again?',
    ifNot: [FLAG_US],
    how: `cpg.computer records when setup was reset. The page asks for a yes or no. ${savedOnly('Run setup again')}`,
    recipe: 'run-setup-again',
    needs: 'Setup can run again now. This page will soon ask cpg.computer whether you did.',
  },
  {
    id: 'set-up-a-routine',
    order: 20,
    title: 'Set up a routine',
    track: 'later',
    say: 'Every Monday at 8, send me the quiet retailers list.',
    happens: [
      'Your agent sets up a routine on your computer.',
      'It runs at the time you said, every week.',
      'You get the list where you asked for it.',
      'You can ask your agent to change or stop it at any time.',
    ],
    check: { kind: 'self' },
    watching: 'Nothing on this page. Tick it once the first list arrives.',
    ifNot: [FLAG_US],
    how: selfTick('Set up a routine', "This page can't see your routines."),
    recipe: 'set-up-a-routine',
    needs: 'Waits on routines on your computer.',
  },
]
